import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Fade, Slide } from '@mui/material'
import FocusTrap from '@mui/material/Unstable_TrapFocus'
import { DateTime } from 'luxon'

import AuthContext from 'context/AuthContext'
import { ANALYTICS_EVENT, GAME_CARD_OFFER_TYPE } from 'constants/index'
import { formatTaskTimeLeft, getTimeDifference, transformTimestampToDate } from 'utils/genericOffer'
import { formatNumber, sendGA4Event } from 'utils/utils'
import { BackArrow } from 'utils/icons'

import useOfferClick from '../useOfferClick'
import ActiveOfferAvatar from './ActiveOfferAvatar'
import useOfferImpression, { offerTileData } from './useOfferImpression'
import { getCurrentTask, getPendingPiggyBanks } from './offerRewards'

/** The category the legacy page reported active offers under, kept so reports stay comparable. */
const ACTIVE_OFFERS_CATEGORY = 'My Games'

/**
 * How many avatars the collapsed bar stacks. More do not fit beside the label on a 375px
 * screen; the label keeps the real count.
 */
const VISIBLE_AVATARS = 4

const Pig = ({ size = 17 }) => (
  <img src="/images/pig.png" alt="piggy banks" width={size} height={size} />
)

export const expiresIn = (activatedOffer) => {
  if (!activatedOffer?.expirationTimestamp) {
    return null
  }

  const expiration = DateTime.fromJSDate(
    transformTimestampToDate(activatedOffer.expirationTimestamp)
  )
  if (expiration <= DateTime.now()) {
    return null
  }

  return formatTaskTimeLeft(getTimeDifference(expiration, DateTime.now()))
}

const InProgressOffer = ({ offer, index, observer, experiment, hasParsingEnabled, onResume }) => {
  const { ref, guid } = useOfferImpression(observer)
  const { handleClick, isDisabled } = useOfferClick({
    offer,
    index,
    type: GAME_CARD_OFFER_TYPE.ACTIVATED,
    offerTileViewGUID: guid,
    experiment,
  })

  const currentTask = getCurrentTask(offer.activatedOffer, { hasParsingEnabled })
  const timeLeft = expiresIn(offer.activatedOffer)

  const resume = () => {
    onResume(offer)
    handleClick()
  }

  return (
    <li
      ref={ref}
      data-offer={offerTileData({
        offer,
        index,
        guid,
        category: ACTIVE_OFFERS_CATEGORY,
        categoryRowNumber: 0,
      })}
      className="gamesListCard gamesRedesignInProgressOffer"
    >
      <ActiveOfferAvatar offer={offer} />
      <span className="gamesRedesignInProgressDetails">
        <span className="gamesRedesignInProgressName">{offer.gameName}</span>
        {currentTask?.piggyBanks > 0 && (
          <span className="gamesRedesignInProgressNext">
            Up next: {formatNumber(currentTask.piggyBanks)}
            <Pig size={14} />
          </span>
        )}
        {timeLeft && <span className="gamesRedesignInProgressExpiry">{timeLeft}</span>}
      </span>
      <button
        type="button"
        className={`gamesRedesignResume ${isDisabled ? 'disabled' : ''}`}
        onClick={resume}
      >
        Resume!
      </button>
    </li>
  )
}

/** The offers in progress live in a bar above the tabs, with an expanding panel. */
const InProgress = ({ offers, observer, experiment }) => {
  const { user } = useContext(AuthContext)
  const navigate = useNavigate()
  const [isOpen, setOpen] = useState(false)

  const triggerRef = useRef(null)

  /**
   * The only way the panel opens or closes, so every path (bar, close button, backdrop and
   * Escape) reports the same event.
   */
  const setPanelOpen = (open) => {
    setOpen(open)
    sendGA4Event(ANALYTICS_EVENT.IN_PROGRESS_BAR_TOGGLE, {
      state: open ? 'open' : 'closed',
      offers_in_progress: offers?.length ?? 0,
      ...experiment,
    })
  }

  // Returned after close: while the focus trap is active it would pull focus back in.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      triggerRef.current?.focus()
    }
    wasOpen.current = isOpen
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const onKeyDown = (event) => event.key === 'Escape' && setPanelOpen(false)
    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  })

  if (!offers?.length) {
    return null
  }

  const pendingPiggyBanks = getPendingPiggyBanks(offers, {
    hasParsingEnabled: user?.hasParsingEnabled,
  })

  const seeAll = () => {
    sendGA4Event(ANALYTICS_EVENT.IN_PROGRESS_SEE_ALL, {
      offers_in_progress: offers.length,
      ...experiment,
    })
    navigate('/library')
  }

  const trackResume = (offer) =>
    sendGA4Event(ANALYTICS_EVENT.IN_PROGRESS_RESUME, {
      offer_id: offer.id,
      unified_app_id: offer.unifiedAppId,
      ...experiment,
    })

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="gamesRedesignInProgressBar"
        aria-expanded={isOpen}
        aria-controls="games-in-progress-panel"
        onClick={() => setPanelOpen(!isOpen)}
      >
        <span className="gamesRedesignInProgressStack">
          {offers.slice(0, VISIBLE_AVATARS).map((offer, index, visible) => (
            <ActiveOfferAvatar
              key={offer.id}
              offer={offer}
              size={36}
              stackOrder={visible.length - index}
            />
          ))}
        </span>
        <span className="gamesRedesignInProgressBarText">
          <span className="gamesRedesignInProgressBarTitle">Continue</span>
          <span className="gamesRedesignInProgressBarLabel">
            {offers.length} offer{offers.length === 1 ? '' : 's'} in progress
          </span>
        </span>
        {pendingPiggyBanks > 0 && (
          <span className="gamesRedesignInProgressBarReward">
            {formatNumber(pendingPiggyBanks)}
            <Pig />
          </span>
        )}
        <span className="gamesRedesignInProgressToggle">
          <BackArrow customClassName={`gamesRedesignInProgressChevron ${isOpen ? 'open' : ''}`} />
        </span>
      </button>

      <Fade in={isOpen} mountOnEnter unmountOnExit>
        <div className="gamesRedesignInProgressOverlay" onClick={() => setPanelOpen(false)} />
      </Fade>
      <FocusTrap open={isOpen} disableRestoreFocus>
        <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
          <div
            id="games-in-progress-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Offers in progress"
            tabIndex={-1}
            className="gamesRedesignInProgressPanel"
          >
            <div className="gamesRedesignInProgressPanelTop">
              <header className="gamesRedesignInProgressPanelHeader">
                <h2 className="gamesRedesignSectionTitle">Continue</h2>
                <button type="button" className="gamesRedesignInProgressSeeAll" onClick={seeAll}>
                  See all
                </button>
                <button
                  type="button"
                  className="gamesRedesignInProgressClose"
                  aria-label="Close offers in progress"
                  onClick={() => setPanelOpen(false)}
                >
                  <BackArrow customClassName="gamesRedesignInProgressChevron open" />
                </button>
              </header>
              <p className="gamesRedesignInProgressSummary">
                {offers.length} offer{offers.length === 1 ? '' : 's'}
                {pendingPiggyBanks > 0 &&
                  ` · ${formatNumber(pendingPiggyBanks)} piggy banks waiting`}
              </p>
            </div>
            <ul className="gamesRedesignInProgressList">
              {offers.map((offer, index) => (
                <InProgressOffer
                  key={offer.id}
                  offer={offer}
                  index={index}
                  observer={observer}
                  experiment={experiment}
                  hasParsingEnabled={user?.hasParsingEnabled}
                  onResume={trackResume}
                />
              ))}
            </ul>
          </div>
        </Slide>
      </FocusTrap>
    </>
  )
}

export default InProgress
